import { create } from "zustand";
import type {
  OscalComponentDefinition,
  OscalComponent,
  OscalCapability,
  OscalResource,
  TreeNode,
  SelectedNode,
} from "./types";

interface VisualizerState {
  // Raw data
  raw: OscalComponentDefinition | null;
  fileName: string;

  // Parsed
  tree: TreeNode | null;
  components: OscalComponent[];
  capabilities: OscalCapability[];
  resources: OscalResource[];

  // UI
  selectedNodeId: string | null;
  selectedNode: SelectedNode | null;
  searchQuery: string;
  expandedNodes: Set<string>;
  filterType: string;

  // Actions
  loadJson: (json: OscalComponentDefinition, fileName: string) => void;
  selectNode: (nodeId: string) => void;
  clearSelection: () => void;
  setSearchQuery: (q: string) => void;
  toggleNode: (nodeId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  setFilterType: (t: string) => void;
  reset: () => void;
}

function buildTree(json: OscalComponentDefinition): TreeNode {
  const def = json["component-definition"];
  const children: TreeNode[] = [];

  // Metadata
  children.push({
    id: "metadata",
    label: "Metadata",
    type: "metadata",
    data: { ...def.metadata } as unknown as Record<string, unknown>,
  });

  // Imports
  const imports = def["import-component-definitions"] || [];
  if (imports.length > 0) {
    children.push({
      id: "imports",
      label: "Imported Definitions",
      type: "imports",
      children: imports.map((imp, idx) => {
        const name = imp.href.split("/").pop()?.replace(".json", "") || imp.href;
        return {
          id: `import-${idx}`,
          label: name,
          type: "import" as const,
          data: { href: imp.href },
        };
      }),
    });
  }

  // Components
  const comps = def.components || [];
  if (comps.length > 0) {
    children.push({
      id: "components",
      label: "Components",
      type: "components",
      children: comps.map((c) => ({
        id: `comp-${c.uuid}`,
        label: c.title,
        type: "component" as const,
        data: { ...c } as unknown as Record<string, unknown>,
      })),
    });
  }

  // Capabilities
  const caps = def.capabilities || [];
  if (caps.length > 0) {
    children.push({
      id: "capabilities",
      label: "Capabilities",
      type: "capabilities",
      children: caps.map((c) => ({
        id: `cap-${c.uuid}`,
        label: c.name,
        type: "capability" as const,
        data: { ...c } as unknown as Record<string, unknown>,
      })),
    });
  }

  // Resources
  const resources = def["back-matter"]?.resources || [];
  if (resources.length > 0) {
    children.push({
      id: "resources",
      label: "Resources",
      type: "resources",
      children: resources.map((r) => ({
        id: `res-${r.uuid}`,
        label: r.title || r.uuid.slice(0, 8),
        type: "resource" as const,
        data: { ...r } as unknown as Record<string, unknown>,
      })),
    });
  }

  return {
    id: "root",
    label: def.metadata?.title || "Component Definition",
    type: "root",
    children,
  };
}

function collectNodeIds(node: TreeNode): string[] {
  const ids = [node.id];
  node.children?.forEach((c) => ids.push(...collectNodeIds(c)));
  return ids;
}

function resolveSelectedNode(
  nodeId: string,
  json: OscalComponentDefinition,
  components: OscalComponent[],
  capabilities: OscalCapability[],
  resources: OscalResource[],
): SelectedNode | null {
  const def = json["component-definition"];

  if (nodeId === "root") {
    return { id: "root", label: def.metadata?.title || "Component Definition", type: "root", data: { uuid: def.uuid } };
  }
  if (nodeId === "metadata") {
    return { id: "metadata", label: "Metadata", type: "metadata", data: { ...def.metadata } as unknown as Record<string, unknown> };
  }
  if (nodeId === "imports") {
    return { id: "imports", label: "Imported Definitions", type: "imports", data: { count: (def["import-component-definitions"] || []).length } };
  }
  if (nodeId.startsWith("import-")) {
    const idx = parseInt(nodeId.replace("import-", ""));
    const imp = (def["import-component-definitions"] || [])[idx];
    if (imp) return { id: nodeId, label: imp.href, type: "import", data: { href: imp.href } };
  }
  if (nodeId === "components") {
    return { id: "components", label: "Components", type: "components", data: { count: components.length } };
  }
  if (nodeId.startsWith("comp-")) {
    const uuid = nodeId.replace("comp-", "");
    const comp = components.find((c) => c.uuid === uuid);
    if (comp) {
      const rels: SelectedNode["relationships"] = [];
      capabilities.forEach((cap) => {
        (cap["incorporates-components"] || []).forEach((ic) => {
          if (ic["component-uuid"] === uuid) {
            rels.push({ label: "Incorporated by", targetId: `cap-${cap.uuid}`, targetLabel: cap.name });
          }
        });
      });
      return {
        id: nodeId,
        label: comp.title,
        type: "component",
        data: {
          uuid: comp.uuid,
          type: comp.type,
          title: comp.title,
          description: comp.description,
          purpose: comp.purpose,
          controlImplementations: comp["control-implementations"]?.length || 0,
        },
        relationships: rels,
      };
    }
  }
  if (nodeId === "capabilities") {
    return { id: "capabilities", label: "Capabilities", type: "capabilities", data: { count: capabilities.length } };
  }
  if (nodeId.startsWith("cap-")) {
    const uuid = nodeId.replace("cap-", "");
    const cap = capabilities.find((c) => c.uuid === uuid);
    if (cap) {
      const rels: SelectedNode["relationships"] = [];
      (cap["incorporates-components"] || []).forEach((ic) => {
        const comp = components.find((c) => c.uuid === ic["component-uuid"]);
        rels.push({
          label: "Incorporates",
          targetId: `comp-${ic["component-uuid"]}`,
          targetLabel: comp?.title || ic["component-uuid"].slice(0, 8),
        });
      });
      return {
        id: nodeId,
        label: cap.name,
        type: "capability",
        data: { uuid: cap.uuid, name: cap.name, description: cap.description },
        relationships: rels,
      };
    }
  }
  if (nodeId === "resources") {
    return { id: "resources", label: "Resources", type: "resources", data: { count: resources.length } };
  }
  if (nodeId.startsWith("res-")) {
    const uuid = nodeId.replace("res-", "");
    const res = resources.find((r) => r.uuid === uuid);
    if (res) return { id: nodeId, label: res.title || uuid, type: "resource", data: { ...res } as unknown as Record<string, unknown> };
  }
  return null;
}

export const useVisualizerStore = create<VisualizerState>((set, get) => ({
  raw: null,
  fileName: "",
  tree: null,
  components: [],
  capabilities: [],
  resources: [],
  selectedNodeId: null,
  selectedNode: null,
  searchQuery: "",
  expandedNodes: new Set(["root", "components", "capabilities", "imports", "resources"]),
  filterType: "",

  loadJson: (json, fileName) => {
    const def = json["component-definition"];
    const tree = buildTree(json);
    set({
      raw: json,
      fileName,
      tree,
      components: def.components || [],
      capabilities: def.capabilities || [],
      resources: def["back-matter"]?.resources || [],
      selectedNodeId: "root",
      selectedNode: { id: "root", label: def.metadata?.title || "Component Definition", type: "root", data: { uuid: def.uuid } },
      expandedNodes: new Set(["root", "components", "capabilities", "imports", "resources", "metadata"]),
    });
  },

  selectNode: (nodeId) => {
    const { raw, components, capabilities, resources } = get();
    if (!raw) return;
    const node = resolveSelectedNode(nodeId, raw, components, capabilities, resources);
    set({ selectedNodeId: nodeId, selectedNode: node });
  },

  clearSelection: () => set({ selectedNodeId: null, selectedNode: null }),

  setSearchQuery: (q) => set({ searchQuery: q }),

  toggleNode: (nodeId) => {
    const expanded = new Set(get().expandedNodes);
    if (expanded.has(nodeId)) expanded.delete(nodeId);
    else expanded.add(nodeId);
    set({ expandedNodes: expanded });
  },

  expandAll: () => {
    const { tree } = get();
    if (!tree) return;
    set({ expandedNodes: new Set(collectNodeIds(tree)) });
  },

  collapseAll: () => set({ expandedNodes: new Set(["root"]) }),

  setFilterType: (t) => set({ filterType: t }),

  reset: () => set({
    raw: null, fileName: "", tree: null, components: [], capabilities: [], resources: [],
    selectedNodeId: null, selectedNode: null, searchQuery: "", expandedNodes: new Set(["root"]),
    filterType: "",
  }),
}));
